import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Inject, Input, Optional, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ToasterService } from 'app/services/toaster.service';
import { CommonUtils } from 'app/utils/commonutils';

@Component({
  selector: 'app-rooms-and-guests',
  templateUrl: './rooms-and-guests.component.html',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    MatIconModule,
    MatDialogModule,
    MatButtonModule
  ]
})
export class RoomsAndGuestsComponent {
  @Output() OnClose = new EventEmitter;
  @Input() paxData: any;
  roomDataList: travellerHotelDTO;
  roomData = {
    rooms: 1,
    totaladult: 2,
    totalchild: 0,
    child_ages: [],
  };
  childAges: any[] = [];
  childYears = Array.from({ length: 17 }, (_, i) => (i + 1).toString()); // 1–12 years
  is_mobile = CommonUtils.isMobile;
  is_tablet = CommonUtils.isTablet;

  /**
   * Component constructor.
   *
   * @param matDialogRef - Optional reference to the Material dialog containing this component. Used to close the dialog on mobile.
   * @param alertService - Service used to show toast/alert messages to the user.
   * @param data - Optional injected dialog data (initial room/pax values or configuration).
   * @returns void
   */
  constructor(
    @Optional() private matDialogRef: MatDialogRef<RoomsAndGuestsComponent>,
    private alertService: ToasterService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any = {}
  ) { }

  /**
   * HostListener to detect window resize events and close the dialog when switching to desktop view
   * @param {Event} event - The resize event
   * @returns {void}
   */
  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.checkAndCloseOnDesktop();
  }

  /**
   * Checks if the current viewport is desktop (not mobile and not tablet) and closes the dialog
   * @returns {void}
   */
  private checkAndCloseOnDesktop(): void {
    const isMobile = CommonUtils.isMobile();
    const isTablet = CommonUtils.isTablet();

    // Close dialog if not on mobile or tablet (i.e., desktop)
    if (!isMobile && !isTablet) {
      this.matDialogRef.close();
    } else {
      if (!isMobile) {
        this.matDialogRef.removePanelClass('full-screen-model');
        this.matDialogRef.addPanelClass('remove-dialog-pading');
      } else {
        this.matDialogRef.addPanelClass('full-screen-model');
        this.matDialogRef.removePanelClass('remove-dialog-pading');
      }
    }
  }
  /**
   * Angular lifecycle hook called after component construction.
   * Initializes local room and passenger (pax) state from injected data, input properties, or localStorage.
   * It also enforces sane defaults for room/adult/child counts and normalizes the child ages array.
   *
   * @returns void
   */
  ngOnInit() {
    let travellerForm: travellerHotelDTO;

    const traveller = new travellerHotelDTO();
    travellerForm = traveller;

    this.roomDataList = travellerForm

    if ((this.data && this.data.roomdata) || (this.paxData && this.paxData.roomdata)) {
      this.roomDataList = this.is_mobile() || this.is_tablet() ? this.data.roomdata : this.paxData.roomdata;
    } else {
      const stored = localStorage.getItem('roomDataListt');
      this.roomDataList = stored ? JSON.parse(stored) : [new travellerHotelDTO()];
    }

    // Ensure at least one room exists
    if (this.roomDataList.rooms === 0) {
      this.roomDataList = new travellerHotelDTO();
    }
    // Set room, adult, and children counts
    this.roomData.rooms = this.roomDataList.rooms;
    this.roomData.totaladult = this.roomDataList.totaladult;
    this.roomData.totalchild = this.roomDataList.totalchild;
    this.roomData.child_ages = this.roomDataList.child_ages == undefined || this.roomDataList.child_ages == null ? [] : this.roomDataList.child_ages;

    this.roomData.child_ages.forEach(x => this.childAges.push({ id: x, year: x + ' year' }));

    // Ensure at least one room exists
    if (!this.roomData.rooms || this.roomData.rooms <= 0) {
      this.roomData.rooms = 1;
    }

    // Ensure adults are at least 1
    if (!this.roomData.totaladult || this.roomData.totaladult <= 0) {
      this.roomData.totaladult = 1;
    }
    // Ensure children are not negative
    if (!this.roomData.totalchild || this.roomData.totalchild < 0) {
      this.roomData.totalchild = 0;
    }

    // Ensure childAges array matches the number of children and uses objects with id and year
    while (this.roomData.child_ages.length < this.roomData.totalchild) {
      this.roomData.child_ages.push("1"); // default "1 year"
    }
    while (this.roomData.child_ages.length > this.roomData.totalchild) {
      this.roomData.child_ages.pop();
    }
  }

  /**
   * Calculate total passengers (adults + children) based on the active room data list.
   * Note: current implementation computes the value but does not return it — it is used by other methods.
   *
   * @returns void
   */
  totalPax() {
    const adult = this.roomDataList.totaladult;
    const child_with_bed = this.roomDataList.totalchild;
    const guest = adult + child_with_bed;
  }

  /**
   * Validate child ages if applicable and then close the component/dialog.
   * On mobile the dialog is closed via MatDialogRef; on desktop the selection is emitted via the OnClose EventEmitter.
   *
   * @returns void
   */
  close(): void {
    // Validate child ages if there are children
    if (this.roomData.totalchild > 0 && this.roomData.child_ages.length !== this.roomData.totalchild) {
      this.alertService.showToast('error', 'Please select the age of the child');
      return;
    }

    // Calculate total guests
    const guest = this.roomData.totaladult + this.roomData.totalchild;
    const result = { allVal: this.roomData.rooms, guest, roomDataList: this.roomData };

    // Check if mobile view once and reuse the result
    const isMobile = this.is_mobile();
    const isTablet = this.is_tablet();

    if ((isMobile || isTablet) && this.matDialogRef) {
      this.matDialogRef.close(result);
    } else {
      this.OnClose.emit(result);
    }
  }

  /**
   * Increase the specified count (rooms, adults, or children) within allowed limits.
   * When children count increases, a default child age entry is appended.
   *
   * @param type - The counter to increase: 'rooms', 'totaladult' or 'totalchild'.
   * @returns void
   */
  increase(type: 'rooms' | 'totaladult' | 'totalchild') {
    if ((type === 'rooms' && this.roomData.rooms < 10) ||
      ((type === 'totaladult' || type === 'totalchild') && this.roomData[type] < 40)) {

      this.roomData[type]++;

      // If children count increased, add default age
      if (type === 'totalchild') {
        this.roomData.child_ages.push('1'); // default "1 year"
        this.childAges.push({ id: '1', year: '1 year' });
      }
    }
  }

  /**
   * Decrease the specified count (rooms, adults, or children) within allowed limits.
   * When children count decreases, the last child age entry is removed.
   *
   * @param type - The counter to decrease: 'rooms', 'totaladult' or 'totalchild'.
   * @returns void
   */
  decrease(type: 'rooms' | 'totaladult' | 'totalchild') {
    const minValue = type === 'totalchild' ? 0 : 1;
    if (this.roomData[type] > minValue) {
      this.roomData[type]--;
      if (type === 'totalchild') {
        this.roomData.child_ages.pop();
        this.childAges.pop();
      }
    }
  }

  /**
   * Validate the current room vs guest counts and apply the selection.
   * This will update child ages from the UI model and then call close() to emit/return the selection.
   *
   * @returns void
   */
  applySelection() {

    if (this.roomData.rooms > (this.roomData.totaladult + this.roomData.totalchild)) {
      this.alertService.showToast('error', 'Number of rooms cannot be more than total guests (adults + children).');
      return;
    }
    else {
      this.roomData.child_ages.forEach((_, index) => {
        this.roomData.child_ages[index] = this.childAges[index].id;
      });

      this.totalPax();
      this.close();
    }
  }

  /**
   * Placeholder to update the selected child's year value for the given index.
   * Currently empty; in the future this should synchronize UI selection with internal models.
   *
   * @param index - Index of the child in the child_ages array to update.
   * @returns void
   */
  updateChildYears(index) {
  }

}

class travellerHotelDTO {
  rooms: number = 1;
  totaladult: number = 2;
  totalchild: number = 0;
  child_ages: string[] = [];
}